import {Charge, ChargeEvent, ChargeEventObject, ChargeStatus} from '../api/generated';
import {cancellationReasonLabels, refundReasonLabels} from '../lib/labels';
import React from 'react';
import {Typography} from 'antd';
import {EmailLink} from './EmailLink';

const {Text} = Typography;

type ChargeMessageProps = {
  charge: Charge | ChargeEventObject;
  showLastRefunded?: boolean;
};

type TimelineChargeMessageProps = {
  event: ChargeEvent;
  showLastRefunded?: boolean;
};

export const getChargeMessage = ({
  charge,
  showLastRefunded = false
}: ChargeMessageProps): JSX.Element | null => {
  const currency = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: charge.currency
  });
  let message = charge.statusMessage;
  let hasEmail = false;
  const email = (
    <EmailLink email={charge.traceDetails?.userEmail}>{charge.traceDetails?.userEmail}</EmailLink>
  );
  if (charge.status === ChargeStatus.REFUNDED) {
    charge.lastRefundReason && (message = refundReasonLabels[charge.lastRefundReason]);
    if (charge.traceDetails?.userEmail) {
      hasEmail = true;
      message += '\nRefunded by: ';
    }
  }

  if (charge.status === ChargeStatus.CANCELED) {
    if (charge.cancellationReason) {
      message = cancellationReasonLabels[charge.cancellationReason];
    }
  }

  if (charge.status === ChargeStatus.EXPIRED) {
    message = '';
  }

  if (charge.status === ChargeStatus.PARTIALLY_REFUNDED) {
    const refunded = showLastRefunded ? charge.lastRefundAmount! : charge.refundedAmount!;
    message = `${currency.format(refunded / 100)} was refunded.`;
    if (charge.lastRefundReason) {
      message += `\nReason: ${charge.lastRefundReason}`;
    }
    if (charge.traceDetails?.userEmail) {
      hasEmail = true;
      message += '\nRefunded by: ';
    }
  }

  if (charge.status === ChargeStatus.PENDING && charge.traceDetails?.userEmail) {
    hasEmail = true;
    message = 'By: ';
  }

  if (!message) return null;
  return (
    <span style={{whiteSpace: 'pre-line'}}>
      {message}
      {hasEmail && email}
    </span>
  );
};

export const TimelineChargeMessage: React.FC<TimelineChargeMessageProps> = ({
  event,
  showLastRefunded = false
}) => {
  if (event.type === 'charge.updated') {
    return null;
  }
  return (
    <Text type="secondary" style={{whiteSpace: 'pre-line'}}>
      {getChargeMessage({charge: event.object, showLastRefunded})}
    </Text>
  );
};
